import { useEffect } from 'react'

export const UNSAVED_CHANGES_MESSAGE =
	'You have unsaved changes. Leave this page without saving?'

const useUnsavedChangesWarning = (
	hasUnsavedChanges,
	message = UNSAVED_CHANGES_MESSAGE,
) => {
	useEffect(() => {
		if (!hasUnsavedChanges) return undefined

		const history = window.history
		const originalPushState = history.pushState
		const originalReplaceState = history.replaceState
		const currentHistoryIndex = history.state?.idx
		let isRestoringHistory = false

		const confirmNavigation = () => window.confirm(message)

		const handleBeforeUnload = (event) => {
			event.preventDefault()
			event.returnValue = ''
		}

		const guardedPushState = (...args) => {
			if (!confirmNavigation()) return undefined
			return originalPushState.apply(history, args)
		}

		const guardedReplaceState = (...args) => {
			if (!confirmNavigation()) return undefined
			return originalReplaceState.apply(history, args)
		}

		const handlePopState = (event) => {
			if (isRestoringHistory) {
				isRestoringHistory = false
				return
			}

			if (confirmNavigation()) return

			const nextHistoryIndex = event.state?.idx
			const canRestoreExactPosition =
				typeof currentHistoryIndex === 'number' &&
				typeof nextHistoryIndex === 'number' &&
				currentHistoryIndex !== nextHistoryIndex

			isRestoringHistory = true
			if (canRestoreExactPosition) {
				history.go(currentHistoryIndex - nextHistoryIndex)
			} else {
				history.forward()
			}
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		window.addEventListener('popstate', handlePopState)
		history.pushState = guardedPushState
		history.replaceState = guardedReplaceState

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
			window.removeEventListener('popstate', handlePopState)
			if (history.pushState === guardedPushState) {
				history.pushState = originalPushState
			}
			if (history.replaceState === guardedReplaceState) {
				history.replaceState = originalReplaceState
			}
		}
	}, [hasUnsavedChanges, message])
}

export default useUnsavedChangesWarning
